import { type IBooleanCondition } from '../../inputs/expressions-editor/conditions/IBooleanCondition';
import { type IAdvancedRichText } from '../../inputs/rich-text-advanced/IAdvancedRichText';
import { type IButtonWidget } from './repository/advanced/buttons/IButtonWidget';
import { type ICompanyWidget } from './repository/advanced/company/ICompanyWidget';
import { type IConfirmWidget } from './repository/advanced/confirm/IConfirmWidget';
import { type IRepeatControllerWidget } from './repository/advanced/repeat-controller/IRepeatControllerWidget';
import { type IRepeatableWidget } from './repository/advanced/repeatable/IRepeatableWidget';
import { type ISubsectionWidget } from './repository/advanced/subsection/ISubsectionWidget';
import { type ITableInputWidget } from './repository/advanced/table-input/ITableInputWidget';
import {
  type IComputedBooleanWidget,
  type IComputedNumberWidget,
  type IComputedStringWidget,
} from './repository/computed/IComputedWidget';
import { type IColumnWidget } from './repository/containers/column/IColumnWidget';
import { type IQuestionWidget } from './repository/containers/question/IQuestionWidget';
import { type IRootWidget } from './repository/containers/root/IRootWidget';
import { type IRowWidget } from './repository/containers/row/IRowWidget';
import { type IDividerWidget } from './repository/graphics/divider/IDividerWidget';
import { type IInfoWidget } from './repository/graphics/info/IInfoWidget';
import { type IPageWidget } from './repository/graphics/page/IPageWidget';
import { type IPageBreakWidget } from './repository/graphics/page-break/IPageBreakWidget';
import { type IParagraphWidget } from './repository/graphics/paragraph/IParagraphWidget';
import { type ISecondaryResumeWidget } from './repository/graphics/secondary-resume/ISecondaryResumeWidget';
import { type IAsyncSelectWidget } from './repository/inputs/async-select/IAsyncSelectWidget';
import { type IBooleanWidget } from './repository/inputs/boolean/IBooleanWidget';
import { type ICurrencyWidget } from './repository/inputs/currency/ICurrencyWidget';
import { type IDateWidget } from './repository/inputs/date/IDateWidget';
import { type IHiddenWidget } from './repository/inputs/hidden/IHiddenWidget';
import { type IMultiSelectWidget } from './repository/inputs/multi-select/IMultiSelectWidget';
import { type INumberWidget } from './repository/inputs/number/INumberWidget';
import { type ISingleSelectWidget } from './repository/inputs/single-select/ISingleSelectWidget';
import { type ITextWidget } from './repository/inputs/text/ITextWidget';

/**
 * Definisce il tipo di elemento strutturale (di input, container, grafico, root...)
 */
export enum WidgetMainType {
  Container = 'container',
  Input = 'input',
  Root = 'root',
  Graphic = 'graphic',
  Base = 'base',
}

/**
 * Tipologia di widget utilizzata per la renderizzazione del
 * menù a tendina.
 */
export enum WidgetCategory {
  Container = 'container',
  Input = 'input',
  Advanced = 'advanced',
  Root = 'root',
  Graphic = 'graphic',
  Computed = 'computed',
}

/**
 * Tipi di elemento, utili per discriminare univocamente l'elemento richiesto.
 */
export enum WidgetType {
  // Root
  Root = 'root',
  // Container
  Row = 'row',
  Column = 'column',
  Question = 'question',
  Repeatable = 'repeatable',
  Subsection = 'subsection',
  // Input
  TableInput = 'tableInput',
  Text = 'text',
  Number = 'number',
  Currency = 'currency',
  Date = 'date',
  Computed = 'computed',
  ComputedBoolean = 'computedBoolean',
  ComputedString = 'computedString',
  ComputedNumber = 'computedNumber',
  Hidden = 'hidden',
  Select = 'select',
  MultiSelect = 'multiSelect',
  AsyncSelect = 'asyncSelect',
  AsyncValue = 'asyncValue',
  AsyncTable = 'asyncTable',
  List = 'list',
  File = 'file',
  Boolean = 'boolean', // checkbox
  Radio = 'radio',
  RadioGroup = 'radioGroup',
  // Graphic
  Info = 'info',
  Divider = 'divider',
  PageBreak = 'pageBreak',
  SecondaryResume = 'secondaryResume',
  Paragraph = 'paragraph',
  Page = 'page',
  // Advanced
  Button = 'button',
  Confirm = 'confirm',
  RepeatController = 'repeatController',
  Switch = 'switch',
  PaymentRow = 'paymentRow',
  Company = 'company',
  Candidate = 'candidate',
  ElectoralListChooser = 'electoralListChooser',
  Vote = 'vote',
}

/** Tipo di votazione gestito dal pulsante di voto: CTS (comitati) o CS (liste). */
export enum VoteType {
  CTS = 'CTS',
  CS = 'CS',
}

/**
 * Union type dei tre tipi base di widget
 */
export type Widget =
  | IBooleanWidget
  | ICurrencyWidget
  | IDateWidget
  | INumberWidget
  | IMultiSelectWidget
  | ISingleSelectWidget
  | IAsyncSelectWidget
  | ITextWidget
  | IDividerWidget
  | IInfoWidget
  | IPageWidget
  | IPageBreakWidget
  | IParagraphWidget
  | ISecondaryResumeWidget
  | IColumnWidget
  | IQuestionWidget
  | IRowWidget
  | IComputedBooleanWidget
  | IComputedStringWidget
  | IComputedNumberWidget
  | IButtonWidget
  | IConfirmWidget
  | IRepeatableWidget
  | ISubsectionWidget
  | ITableInputWidget
  | IRepeatControllerWidget
  | IHiddenWidget
  | ICompanyWidget;

export type WidgetOrRoot = Widget | IRootWidget;

/**
 * Dati contenuti in nel sortable, restutiti dalle funzioni di drag&drop,
 * utili per le operazioni relative allo spostamento dei widget.
 */
export type ISortableWidgetData = Pick<Widget, 'order' | 'path'> & {
  type: WidgetType;
  /** Rappresenta le aree di drop extra (e.g. dei componenti container) */
  directionalDroppableType?: DirectionalDroppableType | null;
  /** Utilizzato dalle aree di drop extra per indicare l'id originale del widget */
  originalId?: string;
};

export enum DirectionalDroppableType {
  Before = 'before',
  After = 'after',
}

export type IWidgetOption = IWidgetOptionValue | IWidgetOptionDivider;

export interface IWidgetOptionValue {
  type: 'option';
  visible?: IBooleanCondition;
  enabled?: IBooleanCondition;
  value: any;
  label: IAdvancedRichText;
}

export function isOptionValue(
  option: IWidgetOption,
): option is IWidgetOptionValue {
  return option.type === 'option';
}

export interface IWidgetOptionDivider {
  type: 'divider';
  visible?: IBooleanCondition;
  label: IAdvancedRichText;
}

export interface IBaseLayoutOptions {
  /** Utilizzato per gestire lo spazio occupato dal widget (in colonne) posizionato in un container Row */
  colSpan?: number;
  /** Utilizzato per gestire la larghezza delle colonne in una tabella */
  tableColumnWidth?: number;
  /**
   * Utilizzato per indicare se il layout é interno ad un tabella
   * Attualmente impostato a true di default, quando viene creato all'interno di una tabella
   * Se in futuro diventa possibile spostare un widget tra una tabella ed il layout andrebbe gestito
   */
  isTableColumn?: boolean;
}

export type IWidgetPath = Array<string | number>;
